import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface TermsOfUseItem {
  id: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  version?: string;
}

export interface PrivacyPolicyItem {
  id: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  version?: string;
}

// 하위 호환성을 위한 인터페이스
export interface TermsOfUse {
  content: string;
  createdAt: Date;
  updatedAt: Date;
  version?: string;
}

export interface PrivacyPolicy {
  content: string;
  createdAt: Date;
  updatedAt: Date;
  version?: string;
}

export interface TermsPrivacy {
  termsOfUse: TermsOfUse;
  privacyPolicy: PrivacyPolicy;
}

export interface TermsPrivacyWithHistory {
  termsOfUse: TermsOfUseItem[];
  privacyPolicy: PrivacyPolicyItem[];
}

const TERMS_PRIVACY_COLLECTION = 'AllUserDoc';
const TERMS_PRIVACY_DOC_ID = 'TermsPrivacy';

export const termsPrivacyService = {
  // 약관 및 개인정보처리방침 가져오기 (버전 히스토리 포함)
  async getTermsPrivacyWithHistory(): Promise<TermsPrivacyWithHistory | null> {
    try {
      const docRef = doc(db, TERMS_PRIVACY_COLLECTION, TERMS_PRIVACY_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // 배열 형식으로 변환 (하위 호환성 지원)
        let termsOfUseArray: TermsOfUseItem[] = [];
        let privacyPolicyArray: PrivacyPolicyItem[] = [];
        
        if (Array.isArray(data.termsOfUse)) {
          termsOfUseArray = data.termsOfUse.map((item: any, index: number) => ({
            id: item.id || `terms-${index}`,
            content: item.content || '',
            createdAt: item.createdAt?.toDate() || new Date(),
            updatedAt: item.updatedAt?.toDate() || new Date(),
            version: item.version || undefined
          }));
        } else if (data.termsOfUse?.content) {
          // 기존 단일 객체 형식 -> 배열로 변환
          termsOfUseArray = [{
            id: 'terms-0',
            content: data.termsOfUse.content || '',
            createdAt: data.termsOfUse.createdAt?.toDate() || new Date(),
            updatedAt: data.termsOfUse.updatedAt?.toDate() || new Date(),
            version: data.termsOfUse.version || undefined
          }];
        }
        
        if (Array.isArray(data.privacyPolicy)) {
          privacyPolicyArray = data.privacyPolicy.map((item: any, index: number) => ({
            id: item.id || `privacy-${index}`,
            content: item.content || '',
            createdAt: item.createdAt?.toDate() || new Date(),
            updatedAt: item.updatedAt?.toDate() || new Date(),
            version: item.version || undefined
          }));
        } else if (data.privacyPolicy?.content) {
          // 기존 단일 객체 형식 -> 배열로 변환
          privacyPolicyArray = [{
            id: 'privacy-0',
            content: data.privacyPolicy.content || '',
            createdAt: data.privacyPolicy.createdAt?.toDate() || new Date(),
            updatedAt: data.privacyPolicy.updatedAt?.toDate() || new Date(),
            version: data.privacyPolicy.version || undefined
          }];
        }
        
        // updatedAt 기준으로 내림차순 정렬 (최신이 먼저)
        termsOfUseArray.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        privacyPolicyArray.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        
        return {
          termsOfUse: termsOfUseArray,
          privacyPolicy: privacyPolicyArray
        };
      }
      
      return { termsOfUse: [], privacyPolicy: [] };
    } catch (error) {
      console.error('약관 및 개인정보처리방침 가져오기 실패:', error);
      return null;
    }
  },

  // 약관 및 개인정보처리방침 가져오기 (하위 호환성 - 최신 버전만)
  async getTermsPrivacy(): Promise<TermsPrivacy | null> {
    try {
      const data = await this.getTermsPrivacyWithHistory();
      if (!data) return null;
      
      return {
        termsOfUse: data.termsOfUse[0] || {
          content: '',
          createdAt: new Date(),
          updatedAt: new Date()
        },
        privacyPolicy: data.privacyPolicy[0] || {
          content: '',
          createdAt: new Date(),
          updatedAt: new Date()
        }
      };
    } catch (error) {
      console.error('약관 및 개인정보처리방침 가져오기 실패:', error);
      return null;
    }
  },

  // 서비스 이용약관 저장 (배열에 추가)
  async saveTermsOfUse(content: string, version?: string): Promise<void> {
    try {
      const docRef = doc(db, TERMS_PRIVACY_COLLECTION, TERMS_PRIVACY_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      const existingData = docSnap.exists() ? docSnap.data() : {};
      
      // 기존 배열 가져오기 또는 변환
      let existingTermsArray: any[] = [];
      if (Array.isArray(existingData.termsOfUse)) {
        existingTermsArray = existingData.termsOfUse;
      } else if (existingData.termsOfUse?.content) {
        // 기존 단일 객체를 배열로 변환
        const existingTermsItem: any = {
          id: 'terms-0',
          content: existingData.termsOfUse.content,
          createdAt: existingData.termsOfUse.createdAt || Timestamp.now(),
          updatedAt: existingData.termsOfUse.updatedAt || Timestamp.now()
        };

        // version이 존재하고 undefined가 아니면 추가
        if (existingData.termsOfUse.version !== undefined && existingData.termsOfUse.version !== null) {
          existingTermsItem.version = existingData.termsOfUse.version;
        }

        existingTermsArray = [existingTermsItem];
      }
      
      // 새 항목 추가 (undefined 값 제거)
      const newItem: any = {
        id: `terms-${Date.now()}`,
        content,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // version이 존재하고 undefined가 아니면 추가
      if (version !== undefined && version !== null) {
        newItem.version = version;
      }
      
      existingTermsArray.push(newItem);
      
      // privacyPolicy 배열 유지
      let privacyPolicyArray: any[] = [];
      if (Array.isArray(existingData.privacyPolicy)) {
        privacyPolicyArray = existingData.privacyPolicy;
      } else if (existingData.privacyPolicy?.content) {
        privacyPolicyArray = [{
          id: 'privacy-0',
          content: existingData.privacyPolicy.content,
          createdAt: existingData.privacyPolicy.createdAt || Timestamp.now(),
          updatedAt: existingData.privacyPolicy.updatedAt || Timestamp.now(),
          version: existingData.privacyPolicy.version
        }];
      } else {
        privacyPolicyArray = [];
      }
      
      const dataToSave = {
        termsOfUse: existingTermsArray,
        privacyPolicy: privacyPolicyArray
      };
      
      console.log('💾 저장할 데이터:', { 
        collection: TERMS_PRIVACY_COLLECTION, 
        docId: TERMS_PRIVACY_DOC_ID,
        hasContent: !!content,
        contentLength: content.length,
        totalTermsItems: existingTermsArray.length
      });
      
      await setDoc(docRef, dataToSave, { merge: true });
      console.log('✅ 저장 완료');
    } catch (error: any) {
      console.error('❌ 서비스 이용약관 저장 실패:', error);
      console.error('에러 상세:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  },

  // 개인정보처리방침 저장 (배열에 추가)
  async savePrivacyPolicy(content: string, version?: string): Promise<void> {
    try {
      const docRef = doc(db, TERMS_PRIVACY_COLLECTION, TERMS_PRIVACY_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      const existingData = docSnap.exists() ? docSnap.data() : {};
      
      // 기존 배열 가져오기 또는 변환
      let existingPrivacyArray: any[] = [];
      if (Array.isArray(existingData.privacyPolicy)) {
        // 기존 배열이 있으면 그대로 사용 (Timestamp는 이미 Firestore 형식)
        existingPrivacyArray = existingData.privacyPolicy;
      } else if (existingData.privacyPolicy?.content) {
        // 기존 단일 객체를 배열로 변환
        const existingCreatedAt = existingData.privacyPolicy.createdAt || Timestamp.now();
        const existingUpdatedAt = existingData.privacyPolicy.updatedAt || Timestamp.now();
        const existingPrivacyItem: any = {
          id: 'privacy-0',
          content: existingData.privacyPolicy.content,
          createdAt: existingCreatedAt instanceof Timestamp ? existingCreatedAt : Timestamp.now(),
          updatedAt: existingUpdatedAt instanceof Timestamp ? existingUpdatedAt : Timestamp.now()
        };

        // version이 존재하고 undefined가 아니면 추가
        if (existingData.privacyPolicy.version !== undefined && existingData.privacyPolicy.version !== null) {
          existingPrivacyItem.version = existingData.privacyPolicy.version;
        }

        existingPrivacyArray = [existingPrivacyItem];
      }
      
      // 새 항목 추가 (undefined 값 제거)
      const newItem: any = {
        id: `privacy-${Date.now()}`,
        content,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // version이 존재하고 undefined가 아니면 추가
      if (version !== undefined && version !== null) {
        newItem.version = version;
      }
      
      existingPrivacyArray.push(newItem);
      
      // termsOfUse 배열 유지
      let termsOfUseArray: any[] = [];
      if (Array.isArray(existingData.termsOfUse)) {
        // 기존 배열이 있으면 그대로 사용
        termsOfUseArray = existingData.termsOfUse;
      } else if (existingData.termsOfUse?.content) {
        const existingTermsCreatedAt = existingData.termsOfUse.createdAt || Timestamp.now();
        const existingTermsUpdatedAt = existingData.termsOfUse.updatedAt || Timestamp.now();
        const existingTermsItemInPrivacy: any = {
          id: 'terms-0',
          content: existingData.termsOfUse.content,
          createdAt: existingTermsCreatedAt instanceof Timestamp ? existingTermsCreatedAt : Timestamp.now(),
          updatedAt: existingTermsUpdatedAt instanceof Timestamp ? existingTermsUpdatedAt : Timestamp.now()
        };

        // version이 존재하고 undefined가 아니면 추가
        if (existingData.termsOfUse.version !== undefined && existingData.termsOfUse.version !== null) {
          existingTermsItemInPrivacy.version = existingData.termsOfUse.version;
        }

        termsOfUseArray = [existingTermsItemInPrivacy];
      } else {
        termsOfUseArray = [];
      }
      
      const dataToSave = {
        termsOfUse: termsOfUseArray,
        privacyPolicy: existingPrivacyArray
      };
      
      console.log('💾 저장할 데이터:', { 
        collection: TERMS_PRIVACY_COLLECTION, 
        docId: TERMS_PRIVACY_DOC_ID,
        hasContent: !!content,
        contentLength: content.length,
        totalPrivacyItems: existingPrivacyArray.length,
        totalTermsItems: termsOfUseArray.length
      });
      
      await setDoc(docRef, dataToSave, { merge: true });
      console.log('✅ 저장 완료');
    } catch (error: any) {
      console.error('❌ 개인정보처리방침 저장 실패:', error);
      console.error('에러 상세:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  },

  // 약관 항목 삭제
  async deleteTermsOfUseItem(itemId: string): Promise<void> {
    try {
      const docRef = doc(db, TERMS_PRIVACY_COLLECTION, TERMS_PRIVACY_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('문서가 존재하지 않습니다.');
      }
      
      const existingData = docSnap.data();
      let termsOfUseArray: any[] = [];
      
      if (Array.isArray(existingData.termsOfUse)) {
        termsOfUseArray = existingData.termsOfUse.filter((item: any) => item.id !== itemId);
      } else if (existingData.termsOfUse?.content) {
        // 단일 객체인 경우 삭제하면 빈 배열
        termsOfUseArray = [];
      }
      
      await setDoc(docRef, {
        termsOfUse: termsOfUseArray,
        privacyPolicy: existingData.privacyPolicy || []
      }, { merge: true });
      
      console.log('✅ 약관 항목 삭제 완료:', itemId);
    } catch (error: any) {
      console.error('❌ 약관 항목 삭제 실패:', error);
      throw error;
    }
  },

  // 개인정보처리방침 항목 삭제
  async deletePrivacyPolicyItem(itemId: string): Promise<void> {
    try {
      const docRef = doc(db, TERMS_PRIVACY_COLLECTION, TERMS_PRIVACY_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('문서가 존재하지 않습니다.');
      }
      
      const existingData = docSnap.data();
      let privacyPolicyArray: any[] = [];
      
      if (Array.isArray(existingData.privacyPolicy)) {
        privacyPolicyArray = existingData.privacyPolicy.filter((item: any) => item.id !== itemId);
      } else if (existingData.privacyPolicy?.content) {
        // 단일 객체인 경우 삭제하면 빈 배열
        privacyPolicyArray = [];
      }
      
      await setDoc(docRef, {
        termsOfUse: existingData.termsOfUse || [],
        privacyPolicy: privacyPolicyArray
      }, { merge: true });
      
      console.log('✅ 개인정보처리방침 항목 삭제 완료:', itemId);
    } catch (error: any) {
      console.error('❌ 개인정보처리방침 항목 삭제 실패:', error);
      throw error;
    }
  },

  // 서비스 이용약관만 가져오기
  async getTermsOfUse(): Promise<TermsOfUse | null> {
    try {
      const data = await this.getTermsPrivacy();
      return data?.termsOfUse || null;
    } catch (error) {
      console.error('서비스 이용약관 가져오기 실패:', error);
      return null;
    }
  },

  // 개인정보처리방침만 가져오기
  async getPrivacyPolicy(): Promise<PrivacyPolicy | null> {
    try {
      const data = await this.getTermsPrivacy();
      return data?.privacyPolicy || null;
    } catch (error) {
      console.error('개인정보처리방침 가져오기 실패:', error);
      return null;
    }
  }
};

